import { useEffect, useRef, useState } from "react";

import { addSeconds, format, startOfDay } from "date-fns";
import { generatePath } from "react-router-dom";

import { getFullTranscript } from "@/api/endpoints/episodes";
import { URLS } from "@/api/urls";
import DownloadIcon from "@/assets/icons/download.svg";
import useDebounce from "@/hooks/useDebounce";
import useResizeObserver from "@/hooks/useResizeObserver";
import Button from "@components/Button";
import Modal, {
  ModalDescription,
  ModalFooter,
  ModalTitle,
} from "@components/Modal";

import styles from "./FullTranscriptModal.module.css";

const timeStampFromSeconds = (seconds) => {
  return format(addSeconds(startOfDay(new Date()), Number(seconds)), "H:mm:ss");
};

const extractTimestamps = (htmlElement) => {
  let leftMostOffset = 0;
  const timestamps = [];

  // extract all span children
  htmlElement.querySelectorAll("span").forEach((item, i) => {
    // Find the left offset of the first item on the first row
    if (i === 0) leftMostOffset = item.offsetLeft;

    // The item that has an offset equal to the frst leftmost is tagged with the first second of this row
    if (item.offsetLeft === leftMostOffset) {
      timestamps.push(timeStampFromSeconds(item.dataset.time));
    }
  });
  return timestamps;
};

/**
 * TODO:
 * The response fom the transcript is a stringified HTML. It's very heavy to do the lookups for the
 * timestamps in the DOM, so if possible we should provide an array of [time, word] instead.
 */
const DefaultFullTranscriptModal = ({
  show,
  onHide,
  episode,
  channel_name,
}) => {
  // states
  const [transcript, setTranscript] = useState();
  const [timestamps, setTimestamps] = useState([]);

  // refs
  const ref = useRef();

  // hooks for handling resizing of the text container
  const { width = 1 } = useResizeObserver({ ref });
  const debouncedWidth = useDebounce(width, 300);

  // fetch full transcript data
  useEffect(() => {
    getFullTranscript(episode.pk).then(({ data }) => {
      setTranscript(data);
    });
  }, [episode.pk, ref]);

  useEffect(() => {
    let mounted = true;
    if (ref.current) {
      // This operation might take a long time and will fail if the component unmounts and
      // tries to run setTimestamps after that.
      const ts = extractTimestamps(ref.current);
      if (mounted) setTimestamps(ts);
    }
    return () => {
      mounted = false;
    };
  }, [debouncedWidth, ref]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      contentLoading={!transcript}
      className={styles.root}
    >
      <ModalDescription>{episode.title}</ModalDescription>
      <ModalTitle>{channel_name}</ModalTitle>
      <div className={styles.transcript}>
        <div className={styles.timings}>
          {timestamps.map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
        <div
          data-testid="full-transcript-text"
          ref={ref}
          dangerouslySetInnerHTML={{ __html: transcript }}
        />
      </div>
      <ModalFooter>
        <Button
          icon={DownloadIcon}
          href={`${generatePath(URLS.FULL_TRANSCRIPT, {
            pk: episode.pk,
          })}.txt`}
        >
          {L.MENTIONS_FULL_TRANSCRIPT_MODAL_DOWNLOAD}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const FullTranscriptModal = (props) =>
  props.show ? <DefaultFullTranscriptModal {...props} /> : null;

export default FullTranscriptModal;
